import React, { useEffect, useState } from "react";
import PageHeader from "../../../../components/common/PageHeader";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import api from "../../../../api/api";

import {
  faSort,
  faSortUp,
  faSortDown,
} from "@fortawesome/free-solid-svg-icons";
import InquiryTable from "../components/InquiryTable";
import { VERTICALS } from "../../../../constants/verticals";
import { toast } from "react-toastify";
import { getClientSegmentMaster } from "../../../client/api/clientService";
import { deleteInquiry, getClientSegmentByVertical, getStageListForInquiry } from "../api/inquiry.api";
import Swal from "sweetalert2";
import { getDashboardCounts, getUserVerticalCounts } from "../../../dashboard/api/dashboardService";
import { useAuth } from "../../../../context/AuthContext";
import { formatCurrency } from "../utils/currency";
import "../styles/inquiry.css";
import "../../../dashboard/Dashboard.css";
import ListPagination from "../../../../components/common/ListPagination";
import {
  DEFAULT_PAGE_SIZE,
  parsePaginationResponse,
} from "../../../../components/common/paginationUtils";

type ListSummary = {
  totalRfqValue: number;
  totalQuotationValue: number;
};

const STATUS_FILTER_OPTIONS = [
  { label: "In Progress", value: "in_progress" },
  { label: "Completed", value: "completed" },
];

const getUrlFilters = (search: string) => {
  const params = new URLSearchParams(search);

  return {
    activity: params.get("activity") || "",
    stage: params.get("stage") || "",
    probability: params.get("probability") || "",
    channel: params.get("channel") || "",
    assigned_to: params.get("assigned_to") || "",
  };
};

const buildInquiryListParams = (appliedFilters: {
  client_name: string;
  contact: string;
  client_segment: number | null;
  stage: string;
  status: string;
  quotation: string;
  assigned_to: string;
  probability: string;
  rfq: string;
  architect: string;
  latest_activity: string;
  scheduled_activity: string;
  activity: string;
  channel: string;
}) => {
  const params: Record<string, string | number> = {};

  Object.entries(appliedFilters).forEach(([key, value]) => {
    if (key === "client_segment") {
      if (value !== null && value !== "") {
        params[key] = value;
      }
      return;
    }

    if (key === "latest_activity") {
      if (value !== null && value !== "") {
        params.last_activity = value;
      }
      return;
    }

    if (value !== null && value !== "") {
      params[key] = value;
    }
  });

  return params;
};

const InquiryList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const { vertical } = useParams<{ vertical: string }>();
  const location = useLocation();

  useEffect(() => {
    const nextUrlFilters = getUrlFilters(location.search);

    setFilters((prev) => {
      const merged = { ...prev, ...nextUrlFilters };
      return JSON.stringify(prev) === JSON.stringify(merged) ? prev : merged;
    });

    setDebouncedFilters((prev) => {
      const merged = { ...prev, ...nextUrlFilters };
      if (JSON.stringify(prev) === JSON.stringify(merged)) return prev;
      setPage(1);
      return merged;
    });
  }, [location.search]);

  const verticalConfig = VERTICALS.find(v => v.key === vertical);

  const verticalId = verticalConfig?.id;
  const verticalName = verticalConfig?.label;

  const canDeleteEnquiry =
    user?.role === "Admin" ||
    user?.role === "CompanyAdmin" ||
    user?.role === "Manager";

  const [verticalSummary, setVerticalSummary] = useState<any>(null);
  const [listSummary, setListSummary] = useState<ListSummary>({
    totalRfqValue: 0,
    totalQuotationValue: 0,
  });

  /* ================= STATE ================= */
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [list, setList] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [showingRecords, setShowingRecords] = useState("");
  const [pageInfo, setPageInfo] = useState("");
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [ordering, setOrdering] = useState("");
  const [clientSegmentOptions, setClientSegmentOptions] = useState<
    { value: number; label: string }[]
  >([]);
  const [stageOptions, setStageOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [assignedToOptions, setAssignedToOptions] = useState<
    { value: string; label: string }[]
  >([]);

  type InquiryFilters = {
    client_name: string;
    contact: string;
    client_segment: number | null;
    stage: string;
    status: string;
    quotation: string;
    assigned_to: string;
    probability: string;
    rfq: string;
    architect: string;
    latest_activity: string;
    scheduled_activity: string;
    activity: string;
    channel: string;
  };

  const createDefaultFilters = (search: string): InquiryFilters => {
    const fromUrl = getUrlFilters(search);

    return {
      client_name: "",
      contact: "",
      client_segment: null,
      stage: fromUrl.stage,
      status: "",
      quotation: location.state?.quotation || "",
      assigned_to: fromUrl.assigned_to,
      probability: fromUrl.probability,
      rfq: location.state?.rfq || "",
      architect: "",
      latest_activity: "",
      scheduled_activity: "",
      activity: fromUrl.activity,
      channel: fromUrl.channel,
    };
  };

  const [filters, setFilters] = useState<InquiryFilters>(() =>
    createDefaultFilters(location.search)
  );

  const [debouncedFilters, setDebouncedFilters] = useState<InquiryFilters>(() =>
    createDefaultFilters(location.search)
  );
  /* ================= SORT HELPERS ================= */
  const getSortIcon = (field: string) => {
    if (ordering === field) return faSortUp;
    if (ordering === `-${field}`) return faSortDown;
    return faSort;
  };

  const toggleOrdering = (field: string) => {
    setPage(1);
    setOrdering((prev) => {
      if (prev === field) return `-${field}`;
      if (prev === `-${field}`) return "";
      return field;
    });
  };

  const loadAssignedToOptions = async () => {
    if (!verticalId) return;

    try {
      const assignees = new Set<string>();
      let pageNo = 1;
      let hasMore = true;

      while (hasMore) {
        const res = await api.get("/crm/v1/glazing-enquiry/", {
          params: {
            page: pageNo,
            page_size: 100,
            vertical: verticalId,
          },
        });

        (res.data.results || []).forEach((row: any) => {
          const assignee = row.inquiry_assigned_to?.trim();
          if (assignee) assignees.add(assignee);
        });

        hasMore = Boolean(res.data.next);
        pageNo += 1;
      }

      setAssignedToOptions(
        Array.from(assignees)
          .sort((a, b) => a.localeCompare(b))
          .map((name) => ({ value: name, label: name }))
      );
    } catch {
      toast.error("Failed to load assignees from inquiries");
    }
  };

  /* ================= API ================= */
  const fetchInquiries = async (
    pageNo: number,
    appliedFilters = debouncedFilters
  ) => {
    if (!verticalId) {
      setIsFetching(false);
      setIsInitialLoad(false);
      return;
    }

    if (isInitialLoad) {
      setIsInitialLoad(true);
    } else {
      setIsFetching(true);
    }

    try {
      const res = await api.get("/crm/v1/glazing-enquiry/", {
        params: {
          page: pageNo,
          page_size: pageSize,
          vertical: verticalId,
          ordering,
          ...buildInquiryListParams(appliedFilters),
        },
      });

      const pagination = parsePaginationResponse(res.data, {
        fallbackPageSize: pageSize,
        fallbackPage: pageNo,
      });

      setList(pagination.results);
      setTotalCount(pagination.totalCount);
      setShowingRecords(pagination.showingRecords);
      setPageInfo(pagination.pageInfo);
      setListSummary({
        totalRfqValue: res.data.summary?.total_dp_details ?? 0,
        totalQuotationValue: res.data.summary?.total_quotation ?? 0,
      });
    } catch {
      toast.error("Failed to load inquiries");
    } finally {
      setIsFetching(false);
      setIsInitialLoad(false);
    }
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPage(1);
  };

  const handleDeleteInquiry = async (id: number) => {
    const result = await Swal.fire({
      title: "Move to Recycle Bin?",
      text: "This enquiry will be moved to the recycle bin instead of being permanently deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, move to bin",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      focusCancel: true,
    });

    if (!result.isConfirmed) return;

    try {
      const res = await deleteInquiry(id);
      toast.success(
        res.data?.message || "Enquiry moved to recycle bin successfully"
      );
      fetchInquiries(page, debouncedFilters);
      loadAssignedToOptions();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
          err?.response?.data?.detail ||
          "You are not allowed to delete this enquiry"
      );
    }
  };

  // debounce filters — only reset page when filter values actually change
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedFilters((prev) => {
        if (JSON.stringify(prev) === JSON.stringify(filters)) {
          return prev;
        }
        setPage(1);
        return filters;
      });
    }, 500);

    return () => clearTimeout(t);
  }, [filters]);

  useEffect(() => {
    const loadVerticalSummary = async () => {
      try {
        if (!verticalId) return;

        let res;

        /* ================= ADMIN ================= */
        if (user?.role?.toLowerCase() === "admin") {
          res = await getDashboardCounts();

          const verticals = res.data?.verticals || {};

          const v =
            Object.values(verticals).find(
              (x: any) => x.vertical_id === verticalId
            ) || null;

          setVerticalSummary(v);
          return;
        }


        /* ================= NON-ADMIN ================= */
        if (!user?.user_id) return;

        res = await getUserVerticalCounts({
          owner_id: user.user_id,
          vertical: verticalId,
        });

        const v = Object.values(res.data?.verticals || {})[0] || null;
        setVerticalSummary(v);

      } catch (err) {
        console.error("Vertical summary load failed", err);
      }
    };

    loadVerticalSummary();
  }, [verticalId, user]);



  // single fetch source
  useEffect(() => {
    fetchInquiries(page, debouncedFilters);
  }, [page, pageSize, ordering, debouncedFilters]);

  useEffect(() => {
    if (!verticalId) return;

    getClientSegmentByVertical(verticalId)
      .then((res) => {
        setClientSegmentOptions(
          res.data.results.map((s: any) => ({
            value: s.id,
            label: s.name, // no need to show vertical name now
          }))
        );
      })
      .catch(() => {
        toast.error("Failed to load client segments");
      });
  }, [verticalId]);

  useEffect(() => {
    const loadStages = async () => {
      try {
        const allStages: { value: string; label: string }[] = [];
        let page = 1;
        let hasMore = true;

        while (hasMore) {
          const res = await getStageListForInquiry({ page });
          const results = res.data.results || [];

          results
            .filter((s: any) => s.is_active)
            .forEach((s: any) => {
              allStages.push({
                value: s.stage_name,
                label: s.stage_name,
              });
            });

          hasMore = Boolean(res.data.next);
          page += 1;
        }

        setStageOptions(allStages);
      } catch {
        toast.error("Failed to load stages");
      }
    };

    loadStages();
  }, []);

  useEffect(() => {
    if (!stageOptions.length || !filters.stage) return;

    const matched = stageOptions.find(
      opt => opt.value.toLowerCase() === filters.stage.toLowerCase()
    );

    if (matched && matched.value !== filters.stage) {
      setFilters(prev => ({ ...prev, stage: matched.value }));
    }
  }, [stageOptions, filters.stage]);

  useEffect(() => {
    loadAssignedToOptions();
  }, [verticalId]);

  return (
    <>
      <PageHeader
        title={`${verticalName} – Sales Inquiry`}

        breadcrumbs={[
          { label: "Home" },
          { label: "Sales" },
          { label: verticalName || "" },

        ]}
      />

      <div className="container mt-4">


        <div className="info-card">
          <div className="info-grid mb-3 vertical-info-grid">
            <InfoBox color="blue" icon="fa-users" label="Inquiries" value={verticalSummary?.inquiries} />
            <InfoBox color="green" icon="fa-chart-line" label="Inquiries Won" value={verticalSummary?.inquiries_won} />
            <InfoBox color="yellow" icon="fa-star" label="High Probability" value={verticalSummary?.high_probability} />
            <InfoBox color="purple" icon="fa-clock" label="RFQ Pending" value={verticalSummary?.rfq_pending} />
            <InfoBox color="orange" icon="fa-file-invoice" label="Quotation Pending" value={verticalSummary?.quotation_pending} />
            <InfoBox color="red" icon="fa-triangle-exclamation" label="Lost Inquiries" value={verticalSummary?.lost_enquiries} />
          </div>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="mb-0"> {`${verticalName} – Inquiry List`}</h4>

            <button
              className="btn btn-primary"
              onClick={() => navigate(`/${vertical}/inquiries/create`)}

            >
              <i className="fas fa-plus me-2"></i>
              Create Inquiry
            </button>
          </div>

          {/* <div className="inquiry-amount-summary info-grid mb-3">
            <InfoBox
              color="teal"
              icon="fa-indian-rupee-sign"
              label="Total RFQ Amount"
              value={`₹${formatCurrency(listSummary.totalRfqValue)}`}
            />
            <InfoBox
              color="indigo"
              icon="fa-file-invoice-dollar"
              label="Total Quotation Amount"
              value={`₹${formatCurrency(listSummary.totalQuotationValue)}`}
            />
          </div> */}

          {/* ================= TABLE ================= */}
          <InquiryTable
            data={list}
            isInitialLoad={isInitialLoad}
            isFetching={isFetching}
            filters={filters}
            setFilters={setFilters}
            toggleOrdering={toggleOrdering}
            getSortIcon={getSortIcon}
            clientSegmentOptions={clientSegmentOptions}
            stageOptions={stageOptions}
            assignedToOptions={assignedToOptions}
            statusOptions={STATUS_FILTER_OPTIONS}
            vertical={vertical!}
            listSummary={listSummary}
            canDeleteEnquiry={canDeleteEnquiry}
            onDelete={handleDeleteInquiry}
          />

          <ListPagination
            page={page}
            totalCount={totalCount}
            pageSize={pageSize}
            currentCount={list.length}
            showingRecords={showingRecords}
            pageInfo={pageInfo}
            onPageChange={setPage}
            onPageSizeChange={handlePageSizeChange}
            className="mt-3"
          />
        </div>
      </div>
    </>
  );
};

export default InquiryList;

const InfoBox = ({ color, icon, label, value }: any) => (
  <div className={`info-box ${color}`}>
    <i className={`fas ${icon}`}></i>
    <strong>{value || 0}</strong>
    <span>{label}</span>
  </div>
);
